import { useCallback, useMemo } from "react";
import { useCustomers } from "./useCustomers.js";
import { useInvoices } from "../../invoices/hooks/useInvoices.js";
import { usePurchases } from "../../purchases/hooks/usePurchases.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { partyInCompany, openingFor } from "../models/customer.js";

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const lower = (v) => String(v || "").trim().toLowerCase();
const balanceOf = (b) => {
  const total = Number(b.grandTotal || 0);
  const paid = Number(b.paid !== undefined ? b.paid : (b.paidAmount || 0));
  return Number(b.balance !== undefined ? b.balance : Math.max(0, total - paid));
};

/** Bills indexed by party id and by party name (older bills may only carry the name). */
function indexBills(bills, idField, nameField) {
  const byId = new Map();
  const byName = new Map();
  bills.forEach((b) => {
    if (b[idField]) byId.set(b[idField], [...(byId.get(b[idField]) || []), b]);
    const n = lower(b[nameField]);
    if (n) byName.set(n, [...(byName.get(n) || []), b]);
  });
  return (party) => {
    const seen = new Set();
    return [...(byId.get(party.id) || []), ...(byName.get(lower(party.name)) || [])].filter((b) => (seen.has(b.id) ? false : seen.add(b.id)));
  };
}

/**
 * Parties of the working company (picked in the header), with each party's due for that company:
 * opening due typed in for the company + the company's open invoice balances (customers) and/or
 * purchase balances (vendors) — the same figure the company's ledger closes on.
 */
export function useCompanyParties() {
  const partyApi = useCustomers();
  const { invoices } = useInvoices();
  const { purchases } = usePurchases();
  const { activeCompanyId, ownsRecord } = useActiveCompany();

  const parties = useMemo(
    () => partyApi.customers.filter((c) => partyInCompany(c, activeCompanyId)),
    [partyApi.customers, activeCompanyId]
  );

  const companyInvoices = useMemo(() => invoices.filter(ownsRecord), [invoices, ownsRecord]);
  const companyPurchases = useMemo(() => purchases.filter(ownsRecord), [purchases, ownsRecord]);
  const invoicesOf = useMemo(() => indexBills(companyInvoices, "customerId", "customerName"), [companyInvoices]);
  const purchasesOf = useMemo(() => indexBills(companyPurchases, "vendorId", "vendorName"), [companyPurchases]);

  const dueById = useMemo(() => {
    const map = new Map();
    parties.forEach((p) => {
      const type = p.partyType || "customer";
      let due = openingFor(p, activeCompanyId);
      if (type !== "vendor") due += invoicesOf(p).reduce((s, b) => s + balanceOf(b), 0);
      if (type === "vendor" || type === "both") due += purchasesOf(p).reduce((s, b) => s + balanceOf(b), 0);
      map.set(p.id, round2(due));
    });
    return map;
  }, [parties, invoicesOf, purchasesOf, activeCompanyId]);

  const dueOf = useCallback((party) => dueById.get(party?.id) ?? 0, [dueById]);

  return {
    ...partyApi,
    allCustomers: partyApi.customers,
    customers: parties,
    dueOf,
    activeCompanyId
  };
}

export default useCompanyParties;
